import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useAnimationConfig, useReducedMotion } from '../lib/animations/context';
import BrandedLoadingAnimation from './BrandedLoadingAnimation';

interface RouteTransitionProps {
  children: React.ReactNode;
  transitionType?: 'slide' | 'fade' | 'scale' | 'blur';
  duration?: number;
  showLoadingScreen?: boolean;
  loadingDuration?: number;
}

const RouteTransition: React.FC<RouteTransitionProps> = ({
  children,
  transitionType = 'fade',
  duration,
  showLoadingScreen = true,
  loadingDuration = 0.5
}) => {
  const location = useLocation();
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const animationDuration = duration || config.customTimings.normal;

  // Handle route changes with loading animation
  useEffect(() => {
    if (showLoadingScreen) {
      setIsLoading(true);
      setLoadingProgress(0);

      // Simulate loading progress
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          const newProgress = prev + Math.random() * 20;
          return newProgress >= 90 ? 90 : newProgress;
        });
      }, 50);

      const loadingTimer = setTimeout(() => {
        clearInterval(progressInterval);
        setLoadingProgress(100);
        
        setTimeout(() => {
          setIsLoading(false);
        }, 200);
      }, loadingDuration * 1000);

      return () => {
        clearInterval(progressInterval);
        clearTimeout(loadingTimer);
      };
    }
  }, [location.pathname, showLoadingScreen, loadingDuration]);

  const getTransitionVariants = () => {
    const baseTransition = {
      duration: animationDuration,
      ease: config.customTimings.defaultEasing
    };

    switch (transitionType) {
      case 'slide':
        return {
          initial: { 
            opacity: 0,
            x: isReducedMotion ? 0 : 100
          },
          animate: { 
            opacity: 1,
            x: 0,
            transition: baseTransition
          },
          exit: { 
            opacity: 0,
            x: isReducedMotion ? 0 : -100,
            transition: baseTransition
          }
        };

      case 'scale':
        return {
          initial: { 
            opacity: 0,
            scale: isReducedMotion ? 1 : 0.95
          },
          animate: { 
            opacity: 1,
            scale: 1,
            transition: baseTransition
          },
          exit: { 
            opacity: 0,
            scale: isReducedMotion ? 1 : 1.05,
            transition: baseTransition
          }
        };

      case 'blur':
        return {
          initial: { 
            opacity: 0,
            filter: isReducedMotion ? 'blur(0px)' : 'blur(10px)'
          },
          animate: { 
            opacity: 1,
            filter: 'blur(0px)',
            transition: baseTransition
          },
          exit: { 
            opacity: 0,
            filter: isReducedMotion ? 'blur(0px)' : 'blur(10px)',
            transition: baseTransition
          }
        };

      case 'fade':
      default:
        return {
          initial: { opacity: 0 },
          animate: { 
            opacity: 1,
            transition: baseTransition
          },
          exit: { 
            opacity: 0,
            transition: baseTransition
          }
        };
    }
  };

  const variants = getTransitionVariants();

  return (
    <>
      {/* Loading Screen */}
      <BrandedLoadingAnimation
        isVisible={isLoading}
        type="page"
        message="Loading page..."
        progress={loadingProgress}
      />

      {/* Page Content with Transitions */}
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="w-full"
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default RouteTransition;